import { bytesToHex, randomBytes, type ExpenseBody } from "@psplit/ledger-core";
import { db, type RecurringTemplate } from "./db.js";

// Month arithmetic on "YYYY-MM" strings; string compare orders correctly.

export function monthOf(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function daysInMonth(month: string): number {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(y, m, 0).getDate();
}

export function nextMonth(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
}

// Months the template is owed an entry for, oldest first. A month is due once
// its (clamped) day has arrived. Missed months are listed, never skipped
// silently: a rent month that vanishes is a wrong ledger.
export function pendingMonths(template: RecurringTemplate, now: Date): string[] {
  const current = monthOf(now);
  const due: string[] = [];
  let month = nextMonth(template.lastApplied === "" ? current : template.lastApplied);
  while (month <= current) {
    const day = Math.min(template.day, daysInMonth(month));
    if (month < current || now.getDate() >= day) due.push(month);
    month = nextMonth(month);
  }
  return due;
}

export async function createTemplate(groupId: string, body: ExpenseBody, day: number): Promise<void> {
  const template: RecurringTemplate = {
    id: bytesToHex(randomBytes(16)),
    groupId,
    body,
    day: Math.max(1, Math.min(31, Math.floor(day))),
    lastApplied: monthOf(new Date()), // starts materializing next month
  };
  await (await db()).put("recurring", template);
}

export async function templatesFor(groupId: string): Promise<RecurringTemplate[]> {
  return (await db()).getAllFromIndex("recurring", "byGroup", groupId);
}

export async function markApplied(id: string, month: string): Promise<void> {
  const d = await db();
  const template = await d.get("recurring", id);
  if (template && template.lastApplied < month) {
    await d.put("recurring", { ...template, lastApplied: month });
  }
}

export async function deleteTemplate(id: string): Promise<void> {
  await (await db()).delete("recurring", id);
}
